package dev.lingvo.site.components.github

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.GitHubModuleDto
import dev.lingvo.shared.localeFlagPrefix
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LCheckbox
import dev.lingvo.site.ds.LDisclosure
import dev.lingvo.site.ds.LFilesIcon
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.ds.transitionColors
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.trf
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * A repository as its owner sees it.
 *
 * **The confirmation screen used to be a list of file paths with checkboxes.** That is the shape the
 * scanner produces, not the shape anybody holds their own repository in — they think "the widget,
 * the iOS app, the shared Compose code", and about languages: "the watch app is English only, the
 * phone app has nine". Reading `composeApp/src/commonMain/composeResources/values-de/strings.xml`
 * forty times to work that out is a job the screen should have done.
 *
 * So the module is the unit here, the languages are the headline, and the paths — still the thing
 * actually being confirmed — sit one disclosure away. Nothing is hidden; it is ordered.
 */
@Composable
fun ModuleCard(
    module: GitHubModuleDto,
    sourceLocale: String?,
    chosen: Set<String>,
    expanded: Boolean,
    onToggleExpanded: () -> Unit,
    onToggleModule: (Boolean) -> Unit,
    onTogglePath: (String, Boolean) -> Unit,
) {
    val palette = ColorMode.current.toSitePalette()
    val selected = module.paths.count { it in chosen }
    val allSelected = selected == module.paths.size && module.paths.isNotEmpty()

    Div(ModuleCardStyle.toModifier().toAttrs()) {
        Column(Modifier.fillMaxWidth().gap(Space.SM)) {
            Row(
                Modifier.fillMaxWidth().gap(Space.SM),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                // The whole module in or out, because that is the decision people actually make.
                // Per-file control still exists, one level down, for the repository where one
                // directory is a fixture set.
                LCheckbox(
                    checked = allSelected,
                    onCheckedChange = onToggleModule,
                    // The label is the module name beside it; the checkbox carries the whole
                    // sentence for anyone who cannot see that relationship.
                    label = "",
                    modifier = Modifier.attr(
                        "aria-label",
                        trf("github.module_cards.import_all_in", "Import everything in {module}", "module" to module.id),
                    ),
                )
                Column(Modifier.gap(2.px)) {
                    Span(
                        Modifier
                            .fontWeight(600)
                            .fontSize(TypeScale.Body)
                            .monoFont()
                            .toAttrs(),
                    ) {
                        Text(module.id)
                    }
                    Span(
                        Modifier
                            .fontSize(TypeScale.Small)
                            .color(palette.textSecondary)
                            .toAttrs(),
                    ) {
                        Text(module.subtitle)
                    }
                }
                Spacer()
                LBadge(
                    text = tPlural(
                        "github.module_cards.count.file",
                        module.fileCount,
                        "{count} file",
                        "{count} files",
                    ),
                    variant = LBadgeVariant.NEUTRAL,
                )
            }

            // **The languages, as the headline.** This is the question somebody opens this screen
            // with — which of my modules is behind, which is English-only — and it was previously
            // answerable only by reading directory names.
            if (module.locales.isEmpty()) {
                Span(
                    Modifier.fontSize(TypeScale.Small).color(palette.textSecondary).toAttrs(),
                ) {
                    Text(t("github.module_cards.no_languages_yet_this_module_ships", "No languages yet — this module ships only its source strings."))
                }
            } else {
                Div(LocaleRowStyle.toModifier().toAttrs()) {
                    module.locales.forEach { code ->
                        val info = localeInfo(code)
                        val isSource = code == sourceLocale
                        // `LBadge`, not a bespoke pill. The hand-rolled one also filled itself
                        // with `surfaceRaised` — the floating-only token used as a flat container
                        // background, the same defect the code block's header comment describes.
                        val sourceSuffix = t("github.module_cards.source", "source")
                        LBadge(
                            text = if (isSource) {
                                "${localeFlagPrefix(info.code)}${info.englishName} · $sourceSuffix"
                            } else {
                                "${localeFlagPrefix(info.code)}${info.englishName}"
                            },
                            variant = if (isSource) LBadgeVariant.ACCENT else LBadgeVariant.NEUTRAL,
                            modifier = Modifier.attr(
                                "title",
                                if (isSource) {
                                    trf(
                                        "github.module_cards.language_is_the_source",
                                        "{language} — the language the source strings are written in",
                                        "language" to info.englishName,
                                    )
                                } else {
                                    info.englishName
                                },
                            ),
                        )
                    }
                }
            }

            // **The exact paths, still one press away — through the shared disclosure.** The
            // hand-rolled one here was a `<button>` carrying `aria-expanded` and nothing else: no
            // `aria-controls`, so a screen reader was never told what the button opened, and the
            // panel it opened was not an element at all until it was already open. `LDisclosure`
            // keeps the panel in the DOM, labelled and referenced, and composes its contents only
            // while it is open — which matters on a scan that can list several hundred paths.
            //
            // The label stops changing with the state. "Hide files" / "Show the 12 files" said in
            // words what `aria-expanded` and the caret already say, and a control whose name moves
            // under the reader is one a keyboard user has to re-read after every press.
            LDisclosure(
                title = tPlural(
                    "github.module_cards.count.file_path",
                    module.paths.size,
                    "{count} file path",
                    "{count} file paths",
                ),
                expanded = expanded,
                onExpandedChange = { onToggleExpanded() },
                icon = { LFilesIcon() },
                // What is chosen inside a closed group is the one fact the closed group hides, and
                // the module checkbox above reads "all" or "none" — it cannot say "nine of twelve".
                badge = if (selected in 1 until module.paths.size) {
                    {
                        LBadge(
                            text = trf(
                                "github.module_cards.selected_of_total_chosen",
                                "{selected} of {total} chosen",
                                "selected" to selected.toString(),
                                "total" to module.paths.size.toString(),
                            ),
                            variant = LBadgeVariant.ACCENT,
                        )
                    }
                } else {
                    null
                },
            ) {
                Column(Modifier.fillMaxWidth().gap(Space.XXS).padding(left = Space.MD)) {
                    module.paths.forEach { path ->
                        LCheckbox(
                            checked = path in chosen,
                            onCheckedChange = { on -> onTogglePath(path, on) },
                            label = path,
                        )
                    }
                }
            }
        }
    }
}

val ModuleCardStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(Space.MD)
            .backgroundColor(colorMode.toSitePalette().surface)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .borderRadius(Radius.Card)
            // In the base, not in `hover`. A transition is read off the style being moved *to*, so
            // declaring it only on hover fades the border in and snaps it back out — and this card
            // sits in a checklist somebody sweeps a cursor down, which is precisely the case
            // `LTableRowStyle` documents fading for: a continuous pointer signal must not flicker.
            .transitionColors()
    }
    hover { Modifier.border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong) }
}

/** Wraps, because a module with twelve languages is the good case, not the exception. */
val LocaleRowStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .styleModifier {
            property("display", "flex")
            property("flex-wrap", "wrap")
            property("gap", Space.XS.toString())
        }
}
